import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatError, fileUrl } from "@/api";
import { Btn, Card, Field, TextArea, Spinner } from "@/components/ui-kit";
import { toast } from "sonner";
import { X, Upload } from "lucide-react";
import { CATEGORY_OPTIONS, SERVICE_OPTIONS } from "@/constants/taxonomy";

const COLLAB_OPTIONS = ["Rahallinen yhteistyö", "Tuotepalkkio", "Rahallinen + tuotepalkkio", "Sovitaan erikseen"];
const WORK_OPTIONS = ["Etänä", "Paikan päällä", "Molemmat"];
const CREATORS_OPTIONS = ["1", "2", "3", "4", "5+"];
export const LANGS = ["Suomi", "Englanti", "Ruotsi"];
const MAX_CATEGORIES = 3;
export const MAX_IMAGES = 2;
export const ACCEPTED = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

export const selectCls = "w-full rounded-xl bg-white border border-[#E6E1D9] px-4 py-3.5 text-[15px] outline-none focus:border-[#292827] transition-colors appearance-none";
export const Label = ({ children }) => <span className="block text-sm font-medium text-[#292827] mb-2">{children}</span>;

export function Section({ title, desc, children, first }) {
  return (
    <section className={first ? "" : "pt-8 mt-8 border-t border-[#E6E1D9]"}>
      <h4 className="text-lg md:text-xl font-semibold tracking-tightish text-[#292827]">{title}</h4>
      {desc && <p className="mt-1.5 text-sm text-[#6E675C] leading-relaxed">{desc}</p>}
      <div className="mt-6 space-y-5">{children}</div>
    </section>
  );
}

export const FieldGroup = ({ label, children }) => (
  <div>{label && <Label>{label}</Label>}{children}</div>
);

export function Pill({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`rounded-full px-4 py-2 text-sm font-medium border transition-colors ${active ? "bg-[#292827] text-white border-[#292827]" : "bg-white text-[#6E675C] border-[#E6E1D9] hover:border-[#292827]"}`}>
      {children}
    </button>
  );
}

function buildBudget(from, to) {
  if (from && to) return `${from}–${to} €`;
  if (from) return `alkaen ${from} €`;
  if (to) return `enintään ${to} €`;
  return "";
}

export default function PublishCampaign() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get("edit");
  const [form, setForm] = useState({
    title: "", description: "", categories: [], services: [], collaboration_type: "",
    budget_from: "", budget_to: "", work_location: "", city: "",
    application_deadline: "", content_deadline: "", creators_needed: "", images: [],
    languages: ["Suomi"], status: "open", website: "",
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fetching, setFetching] = useState(!!editId);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!editId) return;
    api.get(`/campaigns/${editId}`).then((r) => {
      const c = r.data;
      if (c.kind === "affiliate") { navigate(`/dashboard/julkaise-affiliate?edit=${editId}`, { replace: true }); return; }
      setForm({
        title: c.title || "", description: c.description || "",
        categories: c.categories?.length ? c.categories : (c.category ? [c.category] : []),
        services: c.services?.length ? c.services : (c.content_type ? [c.content_type] : []),
        collaboration_type: c.collaboration_type || "",
        budget_from: c.budget_from || "", budget_to: c.budget_to || "",
        work_location: c.work_location || "", city: c.city || "",
        application_deadline: c.application_deadline || "",
        content_deadline: c.content_deadline || c.deadline || "",
        creators_needed: c.creators_needed || "", images: c.images || [],
        languages: c.languages?.length ? c.languages : ["Suomi"], status: c.status || "open",
        website: c.website || "",
      });
      setFetching(false);
    }).catch(() => { toast.error("Kampanjaa ei löytynyt"); navigate("/dashboard/omat-kampanjat"); });
  }, [editId, navigate]);

  const toggle = (key, value, max) => setForm((f) => {
    const has = f[key].includes(value);
    if (!has && max && f[key].length >= max) { toast.error(`Voit valita enintään ${max} kategoriaa`); return f; }
    return { ...f, [key]: has ? f[key].filter((x) => x !== value) : [...f[key], value] };
  });

  const onNumeric = (k, v) => { if (/^\d*$/.test(v)) set(k, v); };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const room = MAX_IMAGES - form.images.length;
    if (room <= 0) { toast.error(`Enintään ${MAX_IMAGES} kuvaa`); return; }
    const valid = files.filter((f) => ACCEPTED.includes(f.type)).slice(0, room);
    if (valid.length < files.length) toast.error("Vain JPG, JPEG, PNG ja WEBP -kuvat sallitaan");
    if (!valid.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of valid) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.path);
      }
      setForm((f) => ({ ...f, images: [...f.images, ...uploaded] }));
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setUploading(false); }
  };
  const removeImage = (i) => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));

  const showCity = form.work_location === "Paikan päällä" || form.work_location === "Molemmat";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.creators_needed) { toast.error("Valitse kuinka monta sisällöntuottajaa etsit"); return; }
    setLoading(true);
    const payload = {
      ...form,
      city: showCity ? form.city : "",
      // legacy display fields for backward compatibility
      category: form.categories[0] || "",
      content_type: form.services[0] || "",
      budget: buildBudget(form.budget_from, form.budget_to),
      location: form.work_location + (showCity && form.city ? ` · ${form.city}` : ""),
      deadline: form.content_deadline,
      delivery_time: "",
    };
    try {
      if (editId) { await api.put(`/campaigns/${editId}`, payload); toast.success("Kampanja päivitetty!"); }
      else { await api.post("/campaigns", payload); toast.success("Kampanja julkaistu!"); }
      navigate("/dashboard/omat-kampanjat");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (fetching) return <Spinner />;

  return (
    <div data-testid="publish-page" className="max-w-3xl">
      <h3 className="text-3xl font-semibold tracking-display text-[#292827] mb-6">{editId ? "Muokkaa kampanjaa" : "Julkaise kampanja"}</h3>
      <Card className="p-8 md:p-10">
        <form onSubmit={submit}>
          <Section title="Kampanjan perustiedot" desc="Anna kampanjalle selkeä otsikko ja kuvaus." first>
            <FieldGroup label="Otsikko">
              <Field value={form.title} required onChange={(e) => set("title", e.target.value)} data-testid="camp-title" />
            </FieldGroup>
            <FieldGroup label="Kuvaus">
              <TextArea value={form.description} required onChange={(e) => set("description", e.target.value)} data-testid="camp-description" />
              <p className="mt-1.5 text-xs text-[#968F85] leading-relaxed" data-testid="camp-description-help">
                Kuvaile mahdollisimman selkeästi, millaista sisältöä etsit, mitä toivot sisällöntuottajalta ja mitä yhteistyö pitää sisällään. Voit kertoa esimerkiksi kampanjan tavoitteesta, toivotusta sisällöstä, aikataulusta ja mahdollisista erityistoiveista. Tämä kuvaus näkyy sisällöntuottajille kampanjassasi.
              </p>
              <p className="mt-2 text-xs font-medium text-[#3A3733] leading-relaxed" data-testid="camp-description-requirements-help">
                Ilmoitathan kuvauksessa mahdollisista vaatimuksista, kuten vähimmäisseuraajamäärästä, mikäli sellainen on tarpeen.
              </p>
            </FieldGroup>
          </Section>

          <Section title="Kategoriat & palvelut" desc="Valitse aihealueet ja palvelut, joita kampanja koskee." >
            <FieldGroup label={`Kategoriat (valitse enintään ${MAX_CATEGORIES}) · ${form.categories.length}/${MAX_CATEGORIES}`}>
              <div className="flex flex-wrap gap-2" data-testid="camp-categories">
                {CATEGORY_OPTIONS.map((c) => (
                  <Pill key={c} active={form.categories.includes(c)} onClick={() => toggle("categories", c, MAX_CATEGORIES)} testid={`camp-category-${c}`}>{c}</Pill>
                ))}
              </div>
            </FieldGroup>
            <FieldGroup label="Palvelut">
              <div className="flex flex-wrap gap-2" data-testid="camp-services">
                {SERVICE_OPTIONS.map((s) => (
                  <Pill key={s} active={form.services.includes(s)} onClick={() => toggle("services", s)} testid={`camp-service-${s}`}>{s}</Pill>
                ))}
              </div>
            </FieldGroup>
          </Section>

          <Section title="Yhteistyö & budjetti" desc="Kerro yhteistyön tyyppi, tarve ja budjetti." >
            <div className="grid sm:grid-cols-2 gap-5">
              <FieldGroup label="Yhteistyön tyyppi">
                <select className={selectCls} value={form.collaboration_type} onChange={(e) => set("collaboration_type", e.target.value)} data-testid="camp-collaboration">
                  <option value="">Valitse...</option>
                  {COLLAB_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </FieldGroup>
              <FieldGroup label="Kuinka monta sisällöntuottajaa etsit? *">
                <select className={selectCls} value={form.creators_needed} onChange={(e) => set("creators_needed", e.target.value)} data-testid="camp-creators-needed" required>
                  <option value="">Valitse...</option>
                  {CREATORS_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </FieldGroup>
              <FieldGroup label="Budjetti alkaen (€)">
                <Field inputMode="numeric" placeholder="esim. 300" value={form.budget_from} onChange={(e) => onNumeric("budget_from", e.target.value)} data-testid="camp-budget-from" />
              </FieldGroup>
              <FieldGroup label="Budjetti asti (€)">
                <Field inputMode="numeric" placeholder="esim. 1500" value={form.budget_to} onChange={(e) => onNumeric("budget_to", e.target.value)} data-testid="camp-budget-to" />
              </FieldGroup>
            </div>
          </Section>

          <Section title="Toteutus & aikataulu" desc="Missä ja milloin yhteistyö tapahtuu." >
            <div className="grid sm:grid-cols-2 gap-5">
              <FieldGroup label="Toteutustapa">
                <select className={selectCls} value={form.work_location} onChange={(e) => set("work_location", e.target.value)} data-testid="camp-work-location">
                  <option value="">Valitse...</option>
                  {WORK_OPTIONS.map((w) => <option key={w} value={w}>{w}</option>)}
                </select>
              </FieldGroup>
              {showCity && (
                <FieldGroup label="Paikkakunta">
                  <Field value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="camp-city" />
                </FieldGroup>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <FieldGroup label="Hakuaika päättyy">
                <Field type="date" value={form.application_deadline} required onChange={(e) => set("application_deadline", e.target.value)} data-testid="camp-appdeadline" />
              </FieldGroup>
              <FieldGroup label="Sisällön toimitus viimeistään">
                <Field type="date" value={form.content_deadline} required onChange={(e) => set("content_deadline", e.target.value)} data-testid="camp-content-deadline" />
              </FieldGroup>
            </div>
          </Section>

          <Section title="Lisätiedot" desc="Kielet, verkkosivu, kuvat ja kampanjan tila." >
            <FieldGroup label="Kielet">
              <div className="flex flex-wrap gap-2">
                {LANGS.map((l) => (
                  <Pill key={l} active={form.languages.includes(l)} onClick={() => toggle("languages", l)} testid={`lang-${l}`}>{l}</Pill>
                ))}
              </div>
            </FieldGroup>
            <FieldGroup label="Yrityksen verkkosivusto (valinnainen)">
              <Field type="url" inputMode="url" placeholder="esim. https://yritys.fi" value={form.website}
                onChange={(e) => set("website", e.target.value)} data-testid="camp-website" />
            </FieldGroup>
            <FieldGroup label={`Kuvat (valinnainen) · Enintään ${MAX_IMAGES} kuvaa · JPG, PNG, WEBP`}>
              <div className="flex flex-wrap gap-3">
                {form.images.map((src, i) => (
                  <div key={i} className="relative h-24 w-24 rounded-xl overflow-hidden border border-[#E6E1D9]" data-testid="camp-image">
                    <img src={fileUrl(src)} alt="" className="h-full w-full object-cover" />
                    <button type="button" onClick={() => removeImage(i)} data-testid="remove-image"
                      className="absolute top-1 right-1 h-6 w-6 rounded-full bg-white/90 flex items-center justify-center text-[#292827] hover:bg-white"><X size={14} /></button>
                  </div>
                ))}
                {form.images.length < MAX_IMAGES && (
                  <label className="h-24 w-24 rounded-xl border border-dashed border-[#E6E1D9] flex flex-col items-center justify-center gap-1 text-[#968F85] cursor-pointer hover:border-[#292827] hover:text-[#292827] transition-colors" data-testid="camp-image-upload">
                    <Upload size={18} /><span className="text-xs">{uploading ? "Ladataan..." : "Lisää"}</span>
                    <input type="file" accept=".jpg,.jpeg,.png,.webp" multiple disabled={uploading} className="hidden" onChange={onFiles} />
                  </label>
                )}
              </div>
            </FieldGroup>
            <div className="max-w-xs">
              <FieldGroup label="Tila">
                <select className={selectCls} value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="camp-status">
                  <option value="open">Avoin</option>
                  <option value="draft">Luonnos</option>
                  <option value="closed">Suljettu</option>
                </select>
              </FieldGroup>
            </div>
          </Section>

          <div className="pt-8 mt-8 border-t border-[#E6E1D9]">
            <Btn type="submit" disabled={loading || uploading} className="w-full !py-4 !text-[16px] !rounded-2xl shadow-sm" data-testid="publish-submit">
              {loading ? "Tallennetaan..." : editId ? "Tallenna muutokset" : "Julkaise kampanja"}
            </Btn>
          </div>
        </form>
      </Card>
    </div>
  );
}
